interface Props {
  className?: string;
  /** 'outline' uses currentColor; 'metallic' renders the Cirrus "Meteorite" gunmetal livery. */
  variant?: 'outline' | 'metallic';
  /** Unique id suffix so multiple metallic instances don't share gradient defs. */
  id?: string;
}

/**
 * Stylized plan-view silhouette of a single-engine low-wing aircraft
 * (Cirrus SR22-style). `outline` tints via currentColor; `metallic` paints the
 * dark "Meteorite" gunmetal livery with a crystalline shimmer highlight.
 */
export default function AircraftSilhouette({ className, variant = 'outline', id = 'ac' }: Props) {
  const metallic = variant === 'metallic';
  const bodyFill = metallic ? `url(#${id}-body)` : 'currentColor';

  return (
    <svg
      viewBox="0 0 400 400"
      className={className}
      fill={bodyFill}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {metallic && (
        <defs>
          {/* Meteorite: dark gunmetal with a metallic sheen */}
          <linearGradient id={`${id}-body`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#3B4251" />
            <stop offset="38%" stopColor="#20242E" />
            <stop offset="55%" stopColor="#4B5568" />
            <stop offset="72%" stopColor="#22262F" />
            <stop offset="100%" stopColor="#141821" />
          </linearGradient>
          {/* Crystalline shimmer sweep */}
          <linearGradient id={`${id}-shine`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0" />
            <stop offset="48%" stopColor="#ffffff" stopOpacity="0.55" />
            <stop offset="52%" stopColor="#ffffff" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
          </linearGradient>
        </defs>
      )}

      {/* Fuselage */}
      <path d="M200 24c9 0 15 10 17 26 3 20 4 52 4 96v70c0 40-1 78-4 104-2 18-8 30-17 30s-15-12-17-30c-3-26-4-64-4-104v-70c0-44 1-76 4-96 2-16 8-26 17-26z" />
      {/* Main wing (low, slightly swept) */}
      <path d="M182 150 L20 214c-6 2-10 0-10-6l1-14c0-5 3-9 8-10l163-30zM218 150 L380 208c6 2 10 0 10-6l-1-14c0-5-3-9-8-10l-163-24z" />
      {/* Horizontal stabilizer */}
      <path d="M186 322 L92 348c-4 1-7 0-7-4v-8c0-3 2-6 6-6l89-12zM214 322 L308 348c4 1 7 0 7-4v-8c0-3-2-6-6-6l-89-12z" />
      {/* Spinner / prop hub */}
      <circle cx="200" cy="30" r="9" />
      {/* Prop blades */}
      <g opacity="0.5">
        <rect x="196" y="2" width="8" height="56" rx="4" />
        <rect x="172" y="26" width="56" height="8" rx="4" />
      </g>

      {metallic && (
        <>
          {/* Sheen along the fuselage spine */}
          <path
            d="M200 24c9 0 15 10 17 26 3 20 4 52 4 96v70c0 40-1 78-4 104-2 18-8 30-17 30s-15-12-17-30c-3-26-4-64-4-104v-70c0-44 1-76 4-96 2-16 8-26 17-26z"
            fill={`url(#${id}-shine)`}
            opacity="0.35"
          />
          {/* Cabin glass hint */}
          <ellipse cx="200" cy="150" rx="10" ry="34" fill="#0EA5E9" opacity="0.35" />
        </>
      )}
    </svg>
  );
}
